import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  Clock, 
  DollarSign, 
  MapPin, 
  AlertCircle, 
  Fuel,
  Wrench,
  CheckCircle,
  Shield,
  Navigation,
  Calendar,
  Banknote
} from 'lucide-react';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';

interface WetLeaseDetailsDisplayProps {
  // Pricing fields
  hourlyRate?: number;
  monthlyRate?: number;
  depositRequired?: number;
  minimumPeriod?: number;
  maximumPeriod?: number;
  // Operational terms
  crewQualifications?: string;
  standbyRates?: string;
  positioningCosts?: string;
  minimumBilling?: string;
  operationalControl?: string;
  thirdPartyOperations?: string;
  fuelSurchargePolicy?: string;
  maintenanceScopeDetailed?: string;
  geographicLimitations?: string;
  wetLeaseTerms?: any;
  hidePrice?: boolean;
  onRequestInfo?: () => void;
  className?: string;
}

const WetLeaseDetailsDisplay: React.FC<WetLeaseDetailsDisplayProps> = ({
  hourlyRate,
  monthlyRate,
  depositRequired,
  minimumPeriod,
  maximumPeriod,
  crewQualifications,
  standbyRates,
  positioningCosts,
  minimumBilling,
  operationalControl,
  thirdPartyOperations,
  fuelSurchargePolicy,
  maintenanceScopeDetailed,
  geographicLimitations,
  wetLeaseTerms,
  hidePrice = false,
  onRequestInfo,
  className = ''
}) => {
  // Extract pricing from wetLeaseTerms if not passed directly
  const hourly = hourlyRate || wetLeaseTerms?.hourlyRate;
  const monthly = monthlyRate || wetLeaseTerms?.monthlyRate;
  const deposit = depositRequired || wetLeaseTerms?.depositRequired;
  const minPeriod = minimumPeriod || wetLeaseTerms?.minimumPeriod;
  const maxPeriod = maximumPeriod || wetLeaseTerms?.maximumPeriod;

  // Extract from nested wetLeaseTerms if available (backwards compatibility)
  const crewQual = crewQualifications || wetLeaseTerms?.crewQualifications;
  const standby = standbyRates || wetLeaseTerms?.standbyRates;
  const positioning = positioningCosts || wetLeaseTerms?.positioningCosts;
  const minBilling = minimumBilling || wetLeaseTerms?.minimumBilling;
  const opControl = operationalControl || wetLeaseTerms?.operationalControl;
  const thirdParty = thirdPartyOperations || wetLeaseTerms?.thirdPartyOperations;
  const fuelPolicy = fuelSurchargePolicy || wetLeaseTerms?.fuelSurchargePolicy;
  const maintenance = maintenanceScopeDetailed || wetLeaseTerms?.maintenanceScopeDetailed;
  const geoLimits = geographicLimitations || wetLeaseTerms?.geographicLimitations;

  // Check for pricing data
  const hasPricingData = hourly || monthly || deposit || minPeriod || maxPeriod;

  // Don't render if no data
  const hasData = hasPricingData || crewQual || standby || positioning || minBilling || opControl || 
                  thirdParty || fuelPolicy || maintenance || geoLimits;
  
  if (!hasData) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const InfoCard = ({ icon: Icon, title, value, variant = 'default' }: any) => {
    if (!value) return null;
    
    return (
      <Card className={`border-2 ${variant === 'highlight' ? 'border-primary/20 bg-primary/5' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-muted-foreground mb-1">{title}</div>
              <div className="text-sm text-foreground">{value}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <Card className="gradient-card border-2">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Wet Lease Operational Terms</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Rates, crew, operations, and maintenance details
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Lease Rates & Terms Section */}
          {hasPricingData && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Banknote className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Lease Rates & Terms</h3>
              </div>
              {hidePrice ? (
                <BlurredPrivateData
                  placeholderContent={
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      <div className="h-24 bg-muted rounded-lg border-2" />
                      <div className="h-24 bg-muted rounded-lg border-2" />
                      <div className="h-24 bg-muted rounded-lg border-2" />
                    </div>
                  }
                  fieldName="Lease Rates"
                  size="lg"
                  onRequestInfo={onRequestInfo}
                />
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {monthly && (
                    <InfoCard
                      icon={DollarSign}
                      title="Monthly Rate"
                      value={`${formatCurrency(monthly)}/month`}
                      variant="highlight"
                    />
                  )}
                  {hourly && (
                    <InfoCard
                      icon={Clock}
                      title="Hourly Rate"
                      value={`${formatCurrency(hourly)}/hr`}
                      variant="highlight"
                    />
                  )}
                  {deposit && (
                    <InfoCard
                      icon={Banknote}
                      title="Security Deposit"
                      value={formatCurrency(deposit)}
                    />
                  )}
                  {(minPeriod || maxPeriod) && (
                    <InfoCard
                      icon={Calendar}
                      title="Lease Period"
                      value={minPeriod && maxPeriod 
                        ? `${minPeriod} - ${maxPeriod} months` 
                        : minPeriod 
                          ? `Min: ${minPeriod} months` 
                          : `Max: ${maxPeriod} months`}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Crew & Qualifications Section */}
          {crewQual && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Crew & Qualifications</h3>
              </div>
              <InfoCard
                icon={Shield}
                title="Crew Qualifications"
                value={crewQual}
                variant="highlight"
              />
            </div>
          )}

          {/* Operational Terms Section */}
          {(standby || minBilling || opControl) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Operational Terms</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <InfoCard
                  icon={Clock}
                  title="Standby Rates"
                  value={standby}
                />
                <InfoCard
                  icon={DollarSign}
                  title="Minimum Billing"
                  value={minBilling}
                />
              </div>
              {opControl && (
                <InfoCard
                  icon={CheckCircle}
                  title="Operational Control"
                  value={opControl}
                />
              )}
            </div>
          )}

          {/* Positioning & Logistics Section */}
          {(positioning || geoLimits) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Navigation className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Positioning & Logistics</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <InfoCard
                  icon={MapPin}
                  title="Positioning Costs"
                  value={positioning}
                />
                {geoLimits && (
                  <InfoCard
                    icon={MapPin}
                    title="Geographic Limitations"
                    value={geoLimits}
                  />
                )}
              </div>
            </div>
          )}

          {/* Third-Party Operations Section */}
          {thirdParty && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Third-Party Operations</h3>
              </div>
              <InfoCard
                icon={AlertCircle}
                title="Third-Party Operations Policy"
                value={thirdParty}
              />
            </div>
          )}

          {/* Fuel & Surcharges Section */}
          {fuelPolicy && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Fuel className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Fuel & Surcharges</h3>
              </div>
              <InfoCard
                icon={Fuel}
                title="Fuel Surcharge Policy"
                value={fuelPolicy}
              />
            </div>
          )}

          {/* Maintenance Section */}
          {maintenance && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-base">Maintenance Scope</h3>
              </div>
              <InfoCard
                icon={Wrench}
                title="Detailed Maintenance Responsibilities"
                value={maintenance}
                variant="highlight"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WetLeaseDetailsDisplay;
